import { LocalizedLink } from "@/components/LocalizedLink";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

// Import images as ES6 modules
import luxaImage from "@/assets/luxa-series-product-optimized.webp";
import lunaImage from "@/assets/luna-m9-main-optimized.webp";
import lumaImage from "@/assets/luma-series-product.jpg";
import ariaImage from "@/assets/aria-series-product-main-opt.webp";
import aureliusImage from "@/assets/aurelius-series-product-main.jpg";
import aureluxImage from "@/assets/aurelux-series-product-main.jpg";
import venaImage from "@/assets/vena-series-product.jpg";
import k2Image from "@/assets/k2-hero-desert.jpg";
import s1Image from "@/assets/s1-hero-egg.jpg";
import m3Image from "@/assets/m3-hero-main.jpg";
import l1Image from "@/assets/l1-product-main.png";
interface Product {
  name: string;
  model: string;
  slug: string;
  image: string;
  tagline: string;
  category: string;
}

interface RelatedProductsProps {
  currentProduct: string;
  className?: string;
}

const products: Product[] = [
  {
    name: "LUXA",
    model: "M80",
    slug: "/products/luxa",
    image: luxaImage,
    tagline: "Flagship luxury with instant heating and premium automation",
    category: "Flagship"
  },
  {
    name: "LUNA",
    model: "M9",
    slug: "/products/luna",
    image: lunaImage,
    tagline: "Wellness spa experience with therapeutic massage functions",
    category: "Wellness"
  },
  {
    name: "LUMA",
    model: "LUMA-2024",
    slug: "/products/luma",
    image: lumaImage,
    tagline: "Dynamic LED lighting with intelligent ambiance control",
    category: "Tech-Forward"
  },
  {
    name: "ARIA",
    model: "H60",
    slug: "/products/aria",
    image: ariaImage,
    tagline: "Wall-hung masterpiece with gesture control features",
    category: "Minimalist"
  },
  {
    name: "AURELIUS",
    model: "M2",
    slug: "/products/aurelius",
    image: aureliusImage,
    tagline: "Wider comfort design with premium user memory features",
    category: "Premium"
  },
  {
    name: "AURELUX",
    model: "AURELUX",
    slug: "/products/aurelux",
    image: aureluxImage,
    tagline: "Gold standard ultra-luxury with 24K accents and platinum service",
    category: "Ultra-Luxury"
  },
  {
    name: "VENA",
    model: "M80F",
    slug: "/products/vena",
    image: venaImage,
    tagline: "Eco-luxury with sustainable water-saving innovation",
    category: "Eco-Luxury"
  },
  {
    name: "K2",
    model: "K2",
    slug: "/products/k2",
    image: k2Image,
    tagline: "Revolutionary square design with space-saving aesthetics",
    category: "Square Design"
  },
  {
    name: "S1",
    model: "S1",
    slug: "/products/s1",
    image: s1Image,
    tagline: "Award-winning egg-shaped design with premium automation",
    category: "Egg Design"
  },
  {
    name: "M3",
    model: "M3",
    slug: "/products/m3",
    image: m3Image,
    tagline: "Voice control and radar sensor with smart cleaning",
    category: "Tech-Forward"
  },
  {
    name: "L1",
    model: "L1",
    slug: "/products/l1",
    image: l1Image,
    tagline: "Compact design with foot sensor and ADA accessibility",
    category: "Compact"
  }
];

export function RelatedProducts({ currentProduct, className = "" }: RelatedProductsProps) {
  // Filter out current product and get 3 related products
  const relatedProducts = products
    .filter(p => p.name !== currentProduct)
    .slice(0, 3);

  return (
    <section className={`py-16 bg-gradient-to-br from-secondary/20 to-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            You Might Also Like
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore other luxury smart toilet collections from our premium range
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {relatedProducts.map((product) => (
            <Card key={product.slug} className="group hover:shadow-xl transition-all duration-500 overflow-hidden border-border/50">
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-muted to-background">
                <img
                  src={product.image}
                  alt={`${product.name} ${product.model} luxury smart toilet`}
                  className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <CardContent className="p-6">
                <Badge variant="outline" className="mb-3">{product.category}</Badge>
                <h3 className="text-2xl font-bold mb-2">
                  {product.name}
                  <span className="block text-base font-medium text-muted-foreground mt-1">
                    Model {product.model}
                  </span>
                </h3>
                <p className="text-muted-foreground mb-4">{product.tagline}</p>
                <Button asChild className="w-full group/btn">
                  <LocalizedLink to={product.slug}>
                    View Details
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button variant="outline" size="lg" asChild>
            <LocalizedLink to="/smart-toilets">
              View All Products
              <ArrowRight className="w-4 h-4 ml-2" />
            </LocalizedLink>
          </Button>
        </div>
      </div>
    </section>
  );
}
